import type { Word } from "@repo/hindi-shared";
import { letterById, wordById } from "@repo/hindi-shared";
import type { ReactElement } from "react";
import { useEffect } from "react";

import { DualLabel } from "../ui/DualLabel";
import { InkButton } from "../ui/InkButton";
import { InkReveal } from "../ui/InkReveal";

import type { ExerciseProps } from "./types";

/** Wrap every occurrence of `glyph` in a sindoor span within `text`. */
function highlightGlyph(
  text: string,
  glyph: string,
): (string | ReactElement)[] {
  if (glyph === "") return [text];
  const parts = text.split(glyph);
  const nodes: (string | ReactElement)[] = [];
  parts.forEach((part, index) => {
    if (part !== "") nodes.push(part);
    if (index < parts.length - 1) {
      nodes.push(
        <span key={index} className="text-sindoor">
          {glyph}
        </span>,
      );
    }
  });
  return nodes;
}

function exampleWords(exampleWordIds: string[]): Word[] {
  const words: Word[] = [];
  for (const id of exampleWordIds) {
    const word = wordById.get(id);
    if (word !== undefined) words.push(word);
  }
  return words;
}

/**
 * Human-readable English name for a letter's romanization/aria label.
 *
 * A handful of signs (currently just halant) have an intentionally empty
 * `iast` in shared content — halant has no vowel sound of its own to
 * romanize, so there's nothing to spell out (see
 * `shared/src/__tests__/content-lint.test.ts`'s explicit exception). That's
 * correct for shared content, but the web layer must not leave the
 * romanization line — or the "Listen: …" aria-label — blank for a learner
 * who cannot read the glyph yet. Fall back to a plain English name; extend
 * this map if another empty-`iast` letter is ever added.
 */
const FALLBACK_NAME_EN: Record<string, string> = {
  "sign-halant": "halant",
};

/** IAST if present, else a fallback English name for letters with no romanization. */
function displayName(letter: { id: string; iast: string }): string {
  return letter.iast !== "" ? letter.iast : (FALLBACK_NAME_EN[letter.id] ?? "");
}

/**
 * LetterIntro — the first meeting with a new letter: a giant glyph that
 * speaks itself on mount and on tap, its IAST + hint, a few example words
 * with the letter picked out in sindoor, and a fixed-grade continue action.
 */
export function LetterIntro({ exercise, audio, onComplete }: ExerciseProps) {
  const letter = letterById.get(exercise.itemId);
  const audioText = letter?.audioText ?? "";

  useEffect(() => {
    // Mount-only: speak the letter exactly once when this exercise appears.
    // Never call audio.speak from render — only from an effect.
    if (audioText !== "") {
      void audio.speak(audioText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- speak once per mount, not on every audio/audioText identity change
  }, []);

  if (letter === undefined) {
    return null;
  }

  function handleGlyphClick(): void {
    void audio.speak(audioText);
  }

  function handleContinue(): void {
    onComplete({ grade: 2, itemId: exercise.itemId });
  }

  const words = exampleWords(letter.exampleWordIds);

  return (
    <div className="flex flex-col items-center gap-8 text-center">
      <button
        type="button"
        onClick={handleGlyphClick}
        aria-label={`Listen: ${displayName(letter)}`}
        className="font-dev leading-none text-ink"
        style={{ fontSize: "min(38vh, 260px)" }}
      >
        {/* Ink-reveal wipe (see `InkReveal`) — replays per letter via the
            item id key; instant under reduced motion. */}
        <InkReveal revealKey={exercise.itemId}>{letter.glyph}</InkReveal>
      </button>

      <div>
        <p className="font-latin text-2xl text-ink-soft italic">
          {displayName(letter)}
        </p>
        <p lang="en" className="mt-2 font-latin text-base text-ink-soft">
          {letter.hint}
        </p>
      </div>

      {words.length > 0 && (
        <ul lang="hi" className="flex flex-col gap-2">
          {words.map((word) => (
            <li key={word.id} className="font-dev text-2xl text-ink">
              {highlightGlyph(word.devanagari, letter.glyph)}
            </li>
          ))}
        </ul>
      )}

      <InkButton variant="sindoor" onClick={handleContinue}>
        <DualLabel variant="inline" en="Continue →" hi="आगे →" />
      </InkButton>
    </div>
  );
}
